import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import styles from './Loader.module.css';

// Hero images to preload
import img1 from '../../images/מורן וינברגר פרוייקטים 10.webp';
import img2 from '../../images/מורן וינברגר פרוייקטים 9.webp';
import img3 from '../../images/מורן וינברגר פרוייקטים 11.webp';

const heroImages = [img1, img2, img3];

const Loader = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const containerRef = useRef(null);
  const logoRef = useRef(null);
  const textRef = useRef(null);
  const percentRef = useRef(null);
  const lineRef = useRef(null);
  const exitStartedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Preload hero images and track progress
  useEffect(() => {
    let loaded = 0;
    const total = heroImages.length;

    const onLoad = () => {
      loaded++;
      setProgress(Math.round((loaded / total) * 100));
    };

    heroImages.forEach((src) => {
      const img = new Image();
      img.onload = onLoad;
      img.onerror = onLoad;
      img.src = src;
    });

    // Safety timeout - complete after 6s even if images not loaded
    const timeout = setTimeout(() => {
      setProgress(100);
    }, 6000);

    return () => clearTimeout(timeout);
  }, []);

  // Entrance animation
  useEffect(() => {
    const paths = logoRef.current?.querySelectorAll('path');
    if (!paths) return;

    const tl = gsap.timeline();
    const cleanupTargets = [...paths, textRef.current, percentRef.current, lineRef.current];

    // Logo paths draw in
    paths.forEach((path) => {
      const length = path.getTotalLength();
      gsap.set(path, {
        strokeDasharray: length,
        strokeDashoffset: length,
        fill: 'transparent',
      });
    });

    tl.to(paths, {
      strokeDashoffset: 0,
      duration: 2,
      stagger: 0.3,
      ease: 'power2.inOut',
    })
    .to(paths, {
      fill: '#444f47',
      stroke: 'transparent',
      duration: 0.8,
      ease: 'power2.out',
    }, '-=0.5');

    // Text fade in
    tl.fromTo(textRef.current,
      { opacity: 0, y: 15 },
      { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' },
      1.2
    );

    // Percentage fade in
    tl.fromTo(percentRef.current,
      { opacity: 0 },
      { opacity: 1, duration: 0.4, ease: 'power2.out' },
      1.5
    );

    // Line grow
    tl.fromTo(lineRef.current,
      { scaleX: 0 },
      { scaleX: 1, duration: 0.6, ease: 'power2.out' },
      1.3
    );

    return () => {
      tl.kill();
      gsap.killTweensOf(cleanupTargets);
    };
  }, []);

  // Exit animation when progress hits 100
  useEffect(() => {
    if (progress < 100 || exitStartedRef.current) return;

    // Small delay to show 100%
    const timer = setTimeout(() => {
      if (exitStartedRef.current) return;
      exitStartedRef.current = true;

      const tl = gsap.timeline({
        onComplete: () => onCompleteRef.current?.(),
      });

      tl.to(percentRef.current, {
        opacity: 0,
        y: -10,
        duration: 0.3,
        ease: 'power2.in',
      })
      .to(textRef.current, {
        opacity: 0,
        y: -10,
        duration: 0.3,
        ease: 'power2.in',
      }, '-=0.2')
      .to(lineRef.current, {
        scaleX: 0,
        duration: 0.3,
        ease: 'power2.in',
      }, '-=0.2')
      .to(logoRef.current, {
        scale: 0.8,
        opacity: 0,
        duration: 0.5,
        ease: 'power3.in',
      }, '-=0.1')
      .to(containerRef.current, {
        clipPath: 'inset(0 0 100% 0)',
        duration: 0.8,
        ease: 'power4.inOut',
      }, '-=0.2');
    }, 400);

    return () => clearTimeout(timer);
  }, [progress]);

  return (
    <div ref={containerRef} className={styles.container}>
      {/* SVG Logo - stylized "m" with dot */}
      <div ref={logoRef} className={styles.logoWrap}>
        <svg
          viewBox="0 0 200 180"
          className={styles.logoSvg}
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Dot */}
          <path
            d="M 18 130 C 18 122 24 116 32 116 C 40 116 46 122 46 130 C 46 138 40 144 32 144 C 24 144 18 138 18 130 Z"
            stroke="#444f47"
            strokeWidth="1.5"
            fill="transparent"
          />
          {/* First stroke of m */}
          <path
            d="M 50 150 C 50 150 42 90 62 40 C 72 15 84 10 90 10 C 100 10 105 25 100 55 C 92 100 80 150 80 150"
            stroke="#444f47"
            strokeWidth="1.5"
            fill="transparent"
          />
          {/* Second stroke of m */}
          <path
            d="M 80 150 C 80 150 78 90 98 40 C 108 15 120 10 126 10 C 136 10 141 25 136 55 C 128 100 116 150 116 150"
            stroke="#444f47"
            strokeWidth="1.5"
            fill="transparent"
          />
          {/* Outer curve connecting */}
          <path
            d="M 116 150 C 116 150 170 148 180 80 C 186 40 172 8 150 4 C 135 2 126 10 126 10"
            stroke="#444f47"
            strokeWidth="1"
            fill="transparent"
          />
          {/* Top dot */}
          <path
            d="M 152 28 C 152 20 158 14 166 14 C 174 14 180 20 180 28 C 180 36 174 42 166 42 C 158 42 152 36 152 28 Z"
            stroke="#444f47"
            strokeWidth="1.5"
            fill="transparent"
          />
        </svg>
      </div>

      {/* Decorative line */}
      <div ref={lineRef} className={styles.line} />

      {/* Text */}
      <p ref={textRef} className={styles.text}>החוויה שלכם נטענת</p>

      {/* Percentage */}
      <div ref={percentRef} className={styles.percentWrap}>
        <span className={styles.percent}>{progress}%</span>
      </div>
    </div>
  );
};

export default Loader;
